import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Alert,
} from '@mui/material';
import { Edit, Refresh } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { campaignService } from '../services/api';

const statusColor = (status) => {
  if (status === 'pending') return 'warning';
  if (status === 'active') return 'success';
  if (status === 'rejected') return 'error';
  return 'default';
};

const CampaignSubmissionsPage = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('pending');
  const [toast, setToast] = useState({ open: false, message: '', type: 'success' });

  const fetchRows = async () => {
    try {
      setLoading(true);
      const res = await campaignService.getAllCampaigns({
        status: filterStatus,
        limit: 100,
        page: 1,
      });
      const data = res.data?.data ?? res.data ?? [];
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      setRows([]);
      setToast({ open: true, message: err.response?.data?.error || 'Failed to load submissions', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRows();
  }, [filterStatus]);

  return (
    <Box sx={{ backgroundColor: '#f5f7fa', minHeight: '100%', p: 4 }}>
      <Container maxWidth={false}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 3 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1a202c' }}>
              Campaign Submissions
            </Typography>
            <Typography variant="body2" sx={{ color: '#667085', mt: 0.5 }}>
              Review user-submitted campaigns. Approve to publish on the website.
            </Typography>
          </Box>
          <Button startIcon={<Refresh />} onClick={fetchRows} variant="outlined">
            Refresh
          </Button>
        </Box>

        <Card sx={{ p: 2, mb: 3 }}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Status</InputLabel>
            <Select label="Status" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="active">Approved</MenuItem>
              <MenuItem value="rejected">Rejected</MenuItem>
              <MenuItem value="all">All</MenuItem>
            </Select>
          </FormControl>
        </Card>

        <Card>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Title</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell>Submitter</TableCell>
                    <TableCell>Submitted</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 6, color: '#667085' }}>
                        No campaigns found for this filter.
                      </TableCell>
                    </TableRow>
                  ) : (
                    rows.map((row) => (
                      <TableRow key={row.id} hover>
                        <TableCell sx={{ fontWeight: 600 }}>{row.title}</TableCell>
                        <TableCell>{row.category?.name || row.category_name || '—'}</TableCell>
                        <TableCell>
                          <Typography variant="body2">{row.submitter_name || '—'}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {row.submitter_email}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {row.created_at ? new Date(row.created_at).toLocaleDateString() : '—'}
                        </TableCell>
                        <TableCell>
                          <Chip label={row.status} size="small" color={statusColor(row.status)} />
                        </TableCell>
                        <TableCell align="right">
                          <Button
                            size="small"
                            startIcon={<Edit />}
                            variant="contained"
                            onClick={() => navigate(`/admin/campaign-submissions/${row.id}/review`)}
                            sx={{ backgroundColor: '#2c5f2d', '&:hover': { backgroundColor: '#234d24' } }}
                          >
                            Review
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Card>
      </Container>

      <Snackbar open={toast.open} autoHideDuration={4000} onClose={() => setToast((t) => ({ ...t, open: false }))}>
        <Alert severity={toast.type} onClose={() => setToast((t) => ({ ...t, open: false }))}>
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CampaignSubmissionsPage;
